/**
 * Every board in one project, and how much of each is done.
 *
 * The first screen after a project is chosen, and the one an engineer sees most
 * often — they arrive in a switchroom, find the board they are standing in front
 * of, and go. So boards are listed by tag in the order the register holds them,
 * not sorted into something cleverer that puts MSB-1A somewhere unexpected.
 */

import { memo } from 'react';
import type { Asset } from '../domain/types.js';

export interface BoardProgress {
  /** Breakers on this board with everything answered and every photo taken. */
  complete: number;
  breakers: number;
  shotsCaptured: number;
  shotsRequired: number;
}

export interface BoardListProps {
  projectName: string;
  boards: readonly Asset[];
  progress: Readonly<Record<string, BoardProgress>>;
  onOpen: (board: Asset) => void;
}

export const BoardList = memo(function BoardList({ projectName, boards, progress, onOpen }: BoardListProps) {
  return (
    <section className="boards" aria-label={`${projectName} boards`}>
      <header>
        <h2>{projectName}</h2>
        <p data-testid="board-count">
          {boards.length} board{boards.length === 1 ? '' : 's'}
        </p>
      </header>

      {boards.length === 0 ? (
        // An empty register and a failed read look identical on screen, which is
        // why the freshness banner sits above this and this says only what it
        // knows.
        <p className="empty" data-testid="no-boards">
          No boards in this project yet. Import the register before capture starts.
        </p>
      ) : (
        <ul>
          {boards.map((board) => {
            const p = progress[board.tag];
            const done = p ? p.breakers > 0 && p.complete === p.breakers : false;
            return (
              <li key={board.tag}>
                <button type="button" className="row" onClick={() => onOpen(board)} data-complete={done}>
                  <span className="t">{board.tag}</span>
                  <span className="d" data-testid={`board-progress-${board.tag}`}>
                    {p
                      ? `${p.complete}/${p.breakers} breakers · ${p.shotsCaptured}/${p.shotsRequired} photos`
                      : 'no breakers recorded'}
                  </span>
                  {board.location ? <span className="loc">{board.location}</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
});

/** Rolls per-breaker summaries up to one board. Kept out of the component so it is testable. */
export function boardProgress(
  summaries: readonly { complete: boolean; shotsCaptured: number; shotsRequired: number }[],
): BoardProgress {
  return summaries.reduce<BoardProgress>(
    (acc, s) => ({
      complete: acc.complete + (s.complete ? 1 : 0),
      breakers: acc.breakers + 1,
      shotsCaptured: acc.shotsCaptured + s.shotsCaptured,
      shotsRequired: acc.shotsRequired + s.shotsRequired,
    }),
    { complete: 0, breakers: 0, shotsCaptured: 0, shotsRequired: 0 },
  );
}
